'use client'
import { useState } from 'react'

import { useRequest } from 'ahooks'
import { Avatar } from 'antd'
import { last } from 'lodash'

import { changeUserAvatarApi } from '@eastcoal/api/auth/user/changeUserAvatar'
import { queryFileDetailApi } from '@eastcoal/api/nexus/oss/queryFileDetail'

import { UploadFile } from '@eastcoal/common/components/UploadFile'
import { UploadFileProps } from '@eastcoal/common/components/UploadFile/interface'
import { pick } from '@eastcoal/common/utils/object'

import { useAuthStore } from '@eastcoal/auth/hooks/useStore'

import { MemberType } from '../../enums/MemberType'

import defaultEnterpriseAvatar from './assets/enterpriseAvatar.png'
import defaultPersonAvatar from './assets/personAvatar.png'
import { UserAvatarProps } from './interface'

// 256 KB
const MAX_FILE_SIZE = 256 * 1024

const defaultCropperProps = {
  width: [150, 300],
  height: [150, 300],
  aspectRatio: 1
}

export const UserAvator = ({
  className,
  style,
  disabled = true,
  /* -------------------------------------------------------------------------- */
  /*                                Avatar Props                                */
  /* -------------------------------------------------------------------------- */
  size
}: UserAvatarProps) => {
  const [latestAvatar, setLatestAvatar] = useState<string>()
  const {
    user: { avatarOssId },
    member: { type: memberType }
  } = useAuthStore(state => pick(state, ['user', 'member']))

  useRequest(
    () => queryFileDetailApi({ ossId: avatarOssId }, { silence: true }),
    {
      manual: false,
      ready: !!avatarOssId,
      refreshDeps: [avatarOssId],
      retryCount: 3,
      onSuccess: res => {
        setLatestAvatar(res.accessUrl)
      }
    }
  )

  const handleUploadChange: UploadFileProps['onChange'] = async (_, items) => {
    const value = last(items)
    if (!value) {
      return
    }
    setLatestAvatar(value.url)
    changeUserAvatarApi({ ossId: value.ossId })
  }

  const defaultAvatar =
    memberType === MemberType.ENTERPRISE
      ? defaultEnterpriseAvatar
      : defaultPersonAvatar

  const avatarDom = (
    <Avatar
      draggable
      rootClassName={className}
      size={size}
      src={latestAvatar ?? defaultAvatar}
      style={style}
    />
  )

  return disabled ? (
    avatarDom
  ) : (
    <UploadFile
      cropper={defaultCropperProps}
      maxFileSize={MAX_FILE_SIZE}
      multiple={false}
      showUploadList={false}
      wrapperClassName={className}
      wrapperStyle={style}
      onChange={handleUploadChange}
    >
      {avatarDom}
    </UploadFile>
  )
}
